import { useState } from 'react';
import { Link } from 'react-router';
import { Trophy, Star, Globe, Search, ChevronDown, Calendar } from 'lucide-react';
import { useChannelsStore } from '@/store/useChannelsStore';
import { usePlayerStore } from '@/store/usePlayerStore';
import { ChannelCard } from '@/components/channel/ChannelCard';
import { ChannelGrid } from '@/components/channel/ChannelGrid';
import { PageHeader } from '@/components/common/PageHeader';
import { ChannelGridSkeleton } from '@/components/common/Skeleton';
import { sortChannelsByRelevance } from '@/lib/channels';
import { UpcomingMatches } from '@/components/schedule/UpcomingMatches';

export function HomePage() {
  const sportsChannels = useChannelsStore((s) => s.sportsChannels);
  const isLoading = useChannelsStore((s) => s.isLoadingInitial);
  const setCurrentChannel = usePlayerStore((s) => s.setCurrentChannel);
  const currentChannel = usePlayerStore((s) => s.currentChannel);
  const [visibleCount, setVisibleCount] = useState(20);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-bg-hover rounded w-1/3 animate-pulse" />
        <ChannelGridSkeleton count={8} />
      </div>
    );
  }

  const sorted = sortChannelsByRelevance(sportsChannels);
  const worldCupChannels = sorted.filter((c) => c.broadcasterGroup === 'world-cup');
  const topSportsChannels = sorted.filter((c) => c.broadcasterGroup === 'top-sports');
  // Featured ones get their own sections above; don't repeat them in the
  // "All sports channels" grid below.
  const featuredIds = new Set([
    ...worldCupChannels.map((c) => c.id),
    ...topSportsChannels.map((c) => c.id),
  ]);
  const allChannels = sorted.filter((c) => !featuredIds.has(c.id));

  return (
    <div className="flex flex-col gap-8 h-full">
      {/* Hero / welcome */}
      <PageHeader
        icon={<span aria-hidden="true">🌍</span>}
        title="Sports, live from around the world"
        subtitle={
          <>
            {sportsChannels.length.toLocaleString()} sports channels · powered by{' '}
            <a
              href="https://github.com/iptv-org/iptv"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:underline"
            >
              iptv-org
            </a>
          </>
        }
      />

      {!currentChannel && <UpcomingMatches />}

      {!currentChannel && (
        <div className="flex justify-center -mt-4 mb-2">
          <Link
            to="/matches"
            className="flex items-center gap-2 px-5 py-2 rounded-full bg-bg-hover hover:bg-white/10 text-text-muted hover:text-white transition-colors text-sm"
          >
            <Calendar className="w-4 h-4" />
            View all fixtures
          </Link>
        </div>
      )}

      {/* World Cup broadcasters */}
      {worldCupChannels.length > 0 && (
        <section>
          <PageHeader
            size="section"
            icon={<Trophy className="w-5 h-5 text-accent" />}
            title="World Cup Broadcasters"
            subtitle={`${worldCupChannels.length} curated channel${
              worldCupChannels.length === 1 ? '' : 's'
            }`}
            action={
              <Link
                to="/world-cup"
                className="text-xs text-accent hover:underline whitespace-nowrap"
              >
                View all →
              </Link>
            }
          />
          <div className="grid grid-cols-2 @md:grid-cols-3 @lg:grid-cols-4 @xl:grid-cols-6 gap-4 mt-3 gap-4">
            {worldCupChannels.map((channel) => (
              <ChannelCard
                key={channel.id}
                channel={channel}
                onClick={setCurrentChannel}
                isActive={currentChannel?.id === channel.id}
              />
            ))}
          </div>
        </section>
      )}

      {/* Top Sports broadcasters */}
      {topSportsChannels.length > 0 && (
        <section>
          <PageHeader
            size="section"
            icon={<Star className="w-5 h-5 text-success" />}
            title="Top Sports"
            subtitle={`${topSportsChannels.length} curated channel${
              topSportsChannels.length === 1 ? '' : 's'
            }`}
            action={
              <Link
                to="/top-sports"
                className="text-xs text-accent hover:underline whitespace-nowrap"
              >
                View all →
              </Link>
            }
          />
          <div className="grid grid-cols-2 @md:grid-cols-3 @lg:grid-cols-4 @xl:grid-cols-6 gap-4 mt-3">
            {topSportsChannels.slice(0, 6).map((channel) => (
              <ChannelCard
                key={channel.id}
                channel={channel}
                onClick={setCurrentChannel}
                isActive={currentChannel?.id === channel.id}
              />
            ))}
          </div>
        </section>
      )}

      {/* Browse by sport removed — category grouping doesn't work. */}

      {/* All sports channels */}
      <section>
        <PageHeader
          size="section"
          icon={<Globe className="w-5 h-5 text-text-muted" />}
          title="All sports channels"
          subtitle={`${allChannels.length.toLocaleString()} additional channel${
            allChannels.length === 1 ? '' : 's'
          }`}
          action={
            <Link
              to="/all-channels"
              className="text-xs text-accent hover:underline flex items-center gap-1 whitespace-nowrap"
            >
              <Search className="w-3 h-3" />
              Browse all 10k+ channels
            </Link>
          }
        />
        <div className="min-h-[500px]">
          <ChannelGrid
            channels={allChannels.slice(0, visibleCount)}
            onChannelClick={setCurrentChannel}
            activeChannelId={currentChannel?.id}
            columns={4}
          />
          {visibleCount < allChannels.length && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setVisibleCount((c) => c + 20)}
                className="flex items-center gap-2 px-6 py-2 rounded-full bg-bg-hover hover:bg-white/10 text-text-muted hover:text-white transition-colors text-sm my-4"
              >
                <ChevronDown className="w-4 h-4" />
                Show more
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
